import { createColumnHelper } from '@tanstack/react-table'

import { type ContractDto } from '@b-stock/contract-api-client'
import type { BidPrecision } from '@b-stock/fe-core/bstock-next/hooks'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'

import { SHOW_CONTRACT_QUANTITIES } from '@/buyer-portal/helpers/constants'

import { YourOfferCell } from '../atoms'
import type { OfferingItemRow } from '../types'
import {
  ContractEstimatedMSRP,
  ContractEstimatedQuantity,
  ContractEstimatedTotalMSRP,
  LineAmount,
} from '../values'

const ns = 'Offerings.Details.form'

const columnHelper = createColumnHelper<OfferingItemRow>()

/**
 * Columns:
 * - "Description": item.attributes.description
 * - "Est. Qty": item.quantity (item.quantityUnits in header)
 * - "Est. MSRP" (or COGS): item.attributes.unitRetail
 * - "Est. Total MSRP" (or COGS): unitRetail * item.quantity
 * - "UPC/SKU", item.attributes.ids.sku
 * - "Est. Offer Amount" - LineAmount
 */
export const getContractColumns = ({
  bidPrecision,
  contract,
  currency = 'USD',
}: {
  bidPrecision: BidPrecision
  contract: ContractDto
  currency?: string
}) => {
  const msrpOrCogs = contract.bidUnit === 'COGS' ? 'COGS' : 'MSRP'

  const descriptionColumn = columnHelper.accessor(
    'item.attributes.description',
    {
      cell: (info) => info.getValue(),
      header: () => <FormattedMessage id={`${ns}.headers.description`} />,
    }
  )

  const quantityColumn = columnHelper.accessor('item.quantity', {
    cell: (info) => (
      <ContractEstimatedQuantity value={info.getValue()} contract={contract} />
    ),
    header: () => <FormattedMessage id={`${ns}.headers.estimatedQuantity`} />,
  })

  const msrpColumn = columnHelper.accessor('item.attributes.unitRetail', {
    cell: (info) => (
      <ContractEstimatedMSRP
        value={info.getValue()}
        contract={contract}
        currency={currency}
      />
    ),
    header: () => (
      <FormattedMessage
        id={`${ns}.headers.estMsrpOrCogs`}
        values={{ msrpOrCogs }}
      />
    ),
  })

  const extRetailColumn = columnHelper.accessor('item.attributes.extRetail', {
    cell: (info) => (
      <ContractEstimatedTotalMSRP value={info.getValue()} currency={currency} />
    ),
    header: () => (
      <FormattedMessage
        id={`${ns}.headers.estTotalMsrpOrCogs`}
        values={{ msrpOrCogs }}
      />
    ),
  })

  const upcColumn = columnHelper.accessor((o) => o.item.attributes.ids?.sku, {
    id: 'item.attributes.ids.sku',
    cell: (info) => info.getValue(),
    header: () => <FormattedMessage id={`${ns}.headers.upcSlashSku`} />,
  })

  const yourOfferColumn = columnHelper.accessor((o) => o, {
    id: 'yourOffer',
    cell: (info) => (
      <YourOfferCell
        bidPrecision={bidPrecision}
        contract={contract}
        currency={currency}
        row={info.getValue()}
        rowIndex={info.row.index}
      />
    ),
    header: () => <FormattedMessage id={`${ns}.headers.yourOffer`} />,
    meta: { editable: true, width: { min: 20, fixed: 20 } },
  })

  const lineAmountColumn = columnHelper.accessor((o) => o, {
    id: 'lineAmount',
    cell: (info) => (
      <>
        <LineAmount
          contract={contract}
          currency={currency}
          rowIndex={info.row.index}
        />
        {' /'}
        <FormattedMessage id={`${ns}.headers.order`} />
      </>
    ),
    header: () => (
      <FormattedMessage id={`${ns}.headers.estimatedOfferAmount`} />
    ),
    meta: { align: 'right' },
  })

  if (SHOW_CONTRACT_QUANTITIES) {
    return [
      descriptionColumn,
      quantityColumn,
      msrpColumn,
      extRetailColumn,
      upcColumn,
      yourOfferColumn,
      lineAmountColumn,
    ]
  }

  return [descriptionColumn, yourOfferColumn]
}
